import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../components/shared/PageHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { ConfirmDialog } from '../components/shared/ConfirmDialog';
import { EmptyState } from '../components/shared/EmptyState';
import { UrgencyBadge } from '../components/shared/UrgencyBadge';
import { PdfPreviewModal } from '../components/shared/PdfPreviewModal';
import { ActionsMenu } from '../components/shared/ActionsMenu';
import { useAppContext } from '../lib/store';
import { WorkOrder } from '../lib/types';
import { Plus, Search, Edit, Eye, Trash2, FileText, Copy, FileDown, ChevronDown, Check, X } from 'lucide-react';
// Searchable filter dropdown component
function SearchableFilter({
  label,
  value,
  onValueChange,
  options,
  allLabel
}: {
  label: string;
  value: string;
  onValueChange: (val: string) => void;
  options: {
    value: string;
    label: string;
  }[];
  allLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const filtered = options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()));
  const selectedLabel = value === 'all' ? allLabel : options.find(o => o.value === value)?.label || label;
  return <Popover open={open} onOpenChange={setOpen} data-id="element-646">
      <PopoverTrigger asChild data-id="element-647">
        <Button variant="outline" role="combobox" aria-expanded={open} className="justify-between min-w-[140px] max-w-[200px] font-normal" data-id="element-648">
          <span className="truncate" data-id="element-649">{selectedLabel}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" data-id="element-650" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[220px] p-0" align="start" data-id="element-651">
        <div className="p-2 border-b" data-id="element-652">
          <Input placeholder={`Išči ${label.toLowerCase()}...`} value={search} onChange={e => setSearch(e.target.value)} className="h-8" data-id="element-653" />
        </div>
        <div className="max-h-[200px] overflow-y-auto p-1" data-id="element-654">
          <button className="flex items-center w-full px-2 py-1.5 text-sm rounded-sm hover:bg-slate-100 transition-colors" onClick={() => {
          onValueChange('all');
          setOpen(false);
          setSearch('');
        }} data-id="element-655">
            <Check className={`mr-2 h-4 w-4 ${value === 'all' ? 'opacity-100' : 'opacity-0'}`} data-id="element-656" />
            {allLabel}
          </button>
          {filtered.map(option => <button key={option.value} className="flex items-center w-full px-2 py-1.5 text-sm rounded-sm hover:bg-slate-100 transition-colors" onClick={() => {
          onValueChange(option.value);
          setOpen(false);
          setSearch('');
        }} data-id="element-657">
              <Check className={`mr-2 h-4 w-4 ${value === option.value ? 'opacity-100' : 'opacity-0'}`} data-id="element-658" />
              <span className="truncate" data-id="element-659">{option.label}</span>
            </button>)}
          {filtered.length === 0 && <p className="text-sm text-slate-500 text-center py-4" data-id="element-660">
              Ni zadetkov.
            </p>}
        </div>
      </PopoverContent>
    </Popover>;
}
export function WorkOrderListPage() {
  const navigate = useNavigate();
  const {
    workOrders,
    customers,
    users,
    deleteWorkOrder,
    duplicateWorkOrder
  } = useAppContext();
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [customerFilter, setCustomerFilter] = useState<string>('all');
  const [userFilter, setUserFilter] = useState<string>('all');
  // Modals
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [pdfWorkOrder, setPdfWorkOrder] = useState<WorkOrder | null>(null);
  // Filter options
  const statusOptions = [{
    value: 'Osnutek',
    label: 'Osnutek'
  }, {
    value: 'V delu',
    label: 'V delu'
  }, {
    value: 'Zaključeno',
    label: 'Zaključeno'
  }, {
    value: 'Stornirano',
    label: 'Stornirano'
  }];
  const customerOptions = customers.map(c => ({
    value: c.id,
    label: c.name
  }));
  const userOptions = users.filter(u => u.role === 'Technician' || u.role === 'Admin').map(u => ({
    value: u.id,
    label: u.full_name
  }));
  // Apply filters
  const filteredWorkOrders = workOrders.filter(wo => {
    const matchesSearch = wo.work_order_number.toLowerCase().includes(searchTerm.toLowerCase()) || wo.customer_reference_or_orderer.toLowerCase().includes(searchTerm.toLowerCase()) || wo.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || wo.status === statusFilter;
    const matchesCustomer = customerFilter === 'all' || wo.customer_id === customerFilter;
    const matchesUser = userFilter === 'all' || wo.assigned_user_id === userFilter;
    return matchesSearch && matchesStatus && matchesCustomer && matchesUser;
  });
  const handleDelete = () => {
    if (deleteId) {
      deleteWorkOrder(deleteId);
      setDeleteId(null);
    }
  };
  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setCustomerFilter('all');
    setUserFilter('all');
  };
  const hasActiveFilters = searchTerm || statusFilter !== 'all' || customerFilter !== 'all' || userFilter !== 'all';
  const activeFilterCount = [statusFilter !== 'all', customerFilter !== 'all', userFilter !== 'all'].filter(Boolean).length;
  return <div className="space-y-6" data-id="element-661">
      <PageHeader title="Delovni nalogi" description="Pregled in upravljanje vseh delovnih nalogov." data-id="element-662">
        <Button onClick={() => navigate('/nalogi/nov')} data-id="element-663">
          <Plus className="w-4 h-4 mr-2" data-id="element-664" />
          Nov nalog
        </Button>
      </PageHeader>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border space-y-4" data-id="element-665">
        <div className="relative" data-id="element-666">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-667" />
          <Input placeholder="Iskanje po številki, naročniku ali lokaciji..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9" data-id="element-668" />
        </div>
      </div>

      {/* Desktop Table */}
      <div className="bg-white border rounded-lg hidden md:block" data-id="element-669">
        {filteredWorkOrders.length > 0 ? <Table data-id="element-670">
            <TableHeader data-id="element-671">
              <TableRow data-id="element-672">
                <TableHead data-id="element-673">Številka</TableHead>
                <TableHead data-id="element-674">Datum</TableHead>
                <TableHead data-id="element-675">Stranka</TableHead>
                <TableHead data-id="element-676">Lokacija</TableHead>
                <TableHead data-id="element-677">Izvajalec</TableHead>
                <TableHead data-id="element-678">Nujnost</TableHead>
                <TableHead className="w-[60px]" data-id="element-679"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-id="element-680">
              {filteredWorkOrders.map(wo => {
            const customer = customers.find(c => c.id === wo.customer_id);
            const user = users.find(u => u.id === wo.assigned_user_id);
            return <TableRow key={wo.id} className="cursor-pointer" onClick={() => navigate(`/nalogi/${wo.id}`)} data-id="element-681">
                    <TableCell className="font-medium text-primary" data-id="element-682">
                      {wo.work_order_number}
                    </TableCell>
                    <TableCell data-id="element-683">
                      {new Date(wo.execution_date).toLocaleDateString('sl-SI')}
                    </TableCell>
                    <TableCell className="max-w-[180px] truncate" title={customer?.name} data-id="element-684">
                      {customer?.name}
                    </TableCell>
                    <TableCell className="max-w-[150px] truncate" title={wo.location} data-id="element-685">
                      {wo.location}
                    </TableCell>
                    <TableCell data-id="element-686">{user?.full_name || '/'}</TableCell>
                    <TableCell data-id="element-687">
                      <UrgencyBadge urgency={wo.urgency} data-id="element-688" />
                    </TableCell>
                    <TableCell onClick={e => e.stopPropagation()} data-id="element-689">
                      <ActionsMenu items={[{
                  label: 'Odpri',
                  icon: <Eye className="w-4 h-4" data-id="element-691" />,
                  onClick: () => navigate(`/nalogi/${wo.id}`)
                }, {
                  label: 'Uredi',
                  icon: <Edit className="w-4 h-4" data-id="element-692" />,
                  onClick: () => navigate(`/nalogi/${wo.id}/uredi`)
                }, {
                  label: 'Generiraj PDF',
                  icon: <FileDown className="w-4 h-4" data-id="element-693" />,
                  onClick: () => setPdfWorkOrder(wo)
                }]} data-id="element-690" />
                    </TableCell>
                  </TableRow>;
          })}
            </TableBody>
          </Table> : <EmptyState icon={FileText} title="Ni najdenih nalogov" description={hasActiveFilters ? 'Spremenite filtre in poskusite znova.' : 'V sistemu še ni vnesenih delovnih nalogov.'} actionLabel={hasActiveFilters ? 'Počisti filtre' : 'Nov nalog'} onAction={hasActiveFilters ? clearFilters : () => navigate('/nalogi/nov')} data-id="element-694" />}
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3" data-id="element-695">
        {filteredWorkOrders.length > 0 ? filteredWorkOrders.map(wo => {
        const customer = customers.find(c => c.id === wo.customer_id);
        const user = users.find(u => u.id === wo.assigned_user_id);
        return <div key={wo.id} className="bg-white border rounded-lg p-4 space-y-3" data-id="element-696">
                <div className="flex items-start justify-between" data-id="element-697">
                  <div className="flex-1 cursor-pointer" onClick={() => navigate(`/nalogi/${wo.id}`)} data-id="element-698">
                    <div className="flex items-center gap-2 mb-1" data-id="element-699">
                      <span className="font-semibold text-primary" data-id="element-700">
                        {wo.work_order_number}
                      </span>
                      <UrgencyBadge urgency={wo.urgency} data-id="element-701" />
                    </div>
                    <p className="text-sm text-slate-600" data-id="element-702">{customer?.name}</p>
                  </div>
                  <ActionsMenu items={[{
              label: 'Odpri',
              icon: <Eye className="w-4 h-4" data-id="element-704" />,
              onClick: () => navigate(`/nalogi/${wo.id}`)
            }, {
              label: 'Uredi',
              icon: <Edit className="w-4 h-4" data-id="element-705" />,
              onClick: () => navigate(`/nalogi/${wo.id}/uredi`)
            }, {
              label: 'Generiraj PDF',
              icon: <FileDown className="w-4 h-4" data-id="element-706" />,
              onClick: () => setPdfWorkOrder(wo)
            }]} data-id="element-703" />
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm cursor-pointer" onClick={() => navigate(`/nalogi/${wo.id}`)} data-id="element-707">
                  <div data-id="element-708">
                    <span className="text-slate-500" data-id="element-709">Datum: </span>
                    {new Date(wo.execution_date).toLocaleDateString('sl-SI')}
                  </div>
                  <div data-id="element-710">
                    <span className="text-slate-500" data-id="element-711">Izvajalec: </span>
                    {user?.full_name || '/'}
                  </div>
                  <div className="col-span-2" data-id="element-712">
                    <span className="text-slate-500" data-id="element-713">Lokacija: </span>
                    {wo.location}
                  </div>
                </div>
              </div>;
      }) : <EmptyState icon={FileText} title="Ni najdenih nalogov" description={hasActiveFilters ? 'Spremenite filtre in poskusite znova.' : 'V sistemu še ni vnesenih delovnih nalogov.'} actionLabel={hasActiveFilters ? 'Počisti filtre' : 'Nov nalog'} onAction={hasActiveFilters ? clearFilters : () => navigate('/nalogi/nov')} data-id="element-714" />}
      </div>

      <ConfirmDialog open={!!deleteId} onOpenChange={open => !open && setDeleteId(null)} title="Izbris delovnega naloga" description="Ali ste prepričani, da želite izbrisati ta delovni nalog? Tega dejanja ni mogoče razveljaviti." onConfirm={handleDelete} destructive data-id="element-715" />

      <PdfPreviewModal open={!!pdfWorkOrder} onOpenChange={open => !open && setPdfWorkOrder(null)} workOrder={pdfWorkOrder} data-id="element-716" />
    </div>;
}